import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft, Phone, Clock, CheckCircle, AlertCircle, 
  User, Wrench, Car, Navigation,
  Loader2, Edit, Save, X, Copy, ExternalLink, Upload, Trash2,
  MessageCircle, Send
} from 'lucide-react';
import { ServiceAdvisorService } from '../../services/serviceAdvisorService';
import { supabase } from '../../services/supabaseClient';
import { serviceRoleClient } from '../../services/serviceRoleClient';
import ComplaintJobChat from '../../components/service_advisor/ComplaintJobChat';
import ComplaintCallRecordings from '../../components/service_advisor/ComplaintCallRecordings';
import ComplaintMechanicMap from '../../components/service_advisor/ComplaintMechanicMap';

const dbClient = () => serviceRoleClient || supabase;

async function mapBargainOffers(offers, db, requestId) {
  const rows = Array.isArray(offers) ? offers : [];
  const byMech = new Map();
  rows.forEach((o) => {
    if (!o?.mechanic_id) return;
    if (String(o.proposed_by || '') === 'customer') return;
    const key = String(o.mechanic_id);
    if (!byMech.has(key)) byMech.set(key, o);
  });
  const ids = [...byMech.keys()];
  if (!ids.length) return [];

  const [{ data: mechs }, { data: pings }] = await Promise.all([
    db
      .from('mechanics')
      .select('id, shop_name, mechanic_name, service_center_lat, service_center_lng')
      .in('id', ids),
    requestId
      ? db
          .from('rsa_job_location_pings')
          .select('mechanic_id, latitude, longitude, created_at')
          .eq('service_request_id', requestId)
          .order('created_at', { ascending: false })
          .limit(300)
      : Promise.resolve({ data: [] }),
  ]);

  const mechById = new Map((mechs || []).map((m) => [String(m.id), m]));
  const lastPing = new Map();
  (pings || []).forEach((p) => {
    const key = String(p.mechanic_id || '');
    if (key && !lastPing.has(key)) lastPing.set(key, p);
  });

  return ids.map((id) => {
    const o = byMech.get(id);
    const m = mechById.get(id);
    const ping = lastPing.get(id);
    return {
      id,
      name: o.mechanic_name || m?.shop_name || m?.mechanic_name || 'Partner',
      amount: o.amount,
      status: o.status,
      live_lat: o.live_lat ?? ping?.latitude,
      live_lng: o.live_lng ?? ping?.longitude,
      live_updated_at: o.live_updated_at || ping?.created_at,
      service_center_lat: m?.service_center_lat,
      service_center_lng: m?.service_center_lng,
    };
  });
}

const extractPincodeFromText = (text) => {
  const m6 = String(text || '').match(/\b(\d{6})\b/);
  return m6?.[1] || '';
};

const parseCoord = (lat, lng) => {
  const a = Number(lat);
  const b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  if (a === 0 && b === 0) return null;
  if (a < -90 || a > 90 || b < -180 || b > 180) return null;
  return { lat: a, lng: b };
};

const extractLatLngFromText = (text) => {
  const s = String(text || '');
  const at = s.match(/@(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
  if (at) return parseCoord(at[1], at[2]);
  const q = s.match(/[?&]q=(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
  if (q) return parseCoord(q[1], q[2]);
  const ll = s.match(/(-?\d{1,2}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)/);
  if (ll) return parseCoord(ll[1], ll[2]);
  return null;
};

const customerPinFromComplaint = (c) =>
  parseCoord(c?.pickup_latitude, c?.pickup_longitude) ||
  parseCoord(c?.latitude, c?.longitude) ||
  extractLatLngFromText(c?.location_link) ||
  extractLatLngFromText(c?.pickup_address) ||
  extractLatLngFromText(c?.address);

const pincodeFromComplaintText = (c) =>
  extractPincodeFromText(c?.pincode) ||
  extractPincodeFromText(c?.pickup_address) ||
  extractPincodeFromText(c?.address) ||
  extractPincodeFromText(c?.drop_location) ||
  extractPincodeFromText(c?.service_location) ||
  extractPincodeFromText(c?.customer_location) ||
  extractPincodeFromText(c?.location_link) ||
  '';

const isTowingComplaint = (c) => {
  const t = `${c?.service_type || ''} ${c?.problem || ''} ${c?.service_description || ''}`.toLowerCase();
  return (
    t.includes('towing') ||
    t.includes('tow ') ||
    t.startsWith('tow') ||
    t.includes('flatbed') ||
    t.includes('recovery van') ||
    t.includes('crane')
  );
};

const pickupAddressText = (c) =>
  String(
    c?.pickup_address ||
      c?.address ||
      c?.service_location ||
      c?.customer_location ||
      c?.pickup_location ||
      '',
  ).trim();

const dropAddressText = (c) => String(c?.drop_location || c?.drop_address || '').trim();

const mapsSearchHref = (text, lat, lng) => {
  const pin = parseCoord(lat, lng) || extractLatLngFromText(text);
  if (pin) return `https://www.google.com/maps?q=${pin.lat},${pin.lng}`;
  const q = String(text || '').trim();
  if (!q) return '';
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
};

const pinFromPickup = (c) =>
  parseCoord(c?.pickup_latitude, c?.pickup_longitude) ||
  parseCoord(c?.latitude, c?.longitude) ||
  extractLatLngFromText(c?.location_link) ||
  extractLatLngFromText(c?.pickup_address) ||
  extractLatLngFromText(c?.address);

const pinFromDrop = (c) =>
  parseCoord(c?.drop_latitude, c?.drop_longitude) || extractLatLngFromText(c?.drop_location);

const haversineKm = (a, b) => {
  if (!a || !b) return null;
  const toRad = (n) => (n * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

const formatKm = (km) => {
  if (km == null || !Number.isFinite(km)) return '';
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(km < 10 ? 1 : 0)} km`;
};

const mapsDirHref = (originPin, originText, destPin, destText) => {
  const origin = originPin ? `${originPin.lat},${originPin.lng}` : String(originText || '').trim();
  const dest = destPin ? `${destPin.lat},${destPin.lng}` : String(destText || '').trim();
  if (!origin || !dest) return '';
  return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(dest)}&travelmode=driving`;
};

const partnerDisplayName = (mech, person) => {
  const shop = String(mech?.shop_name || mech?.mechanic_name || mech?.name || '').trim();
  const who = String(person || mech?.mechanic_name || '').trim();
  if (shop && who && shop.toLowerCase() !== who.toLowerCase()) return `${shop} · ${who}`;
  return shop || who || '';
};

const reverseGeocodePincode = async (lat, lng) => {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
      { headers: { Accept: 'application/json' } },
    );
    if (!res.ok) return '';
    const json = await res.json();
    const fromAddr = String(json?.address?.postcode || '').replace(/\D/g, '');
    if (fromAddr.length >= 6) return fromAddr.slice(0, 6);
    return extractPincodeFromText(json?.display_name);
  } catch {
    return '';
  }
};

const ComplaintDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [showPincodeModal, setShowPincodeModal] = useState(false);
  const [pincode, setPincode] = useState('');
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionData, setCompletionData] = useState({
    completionNotes: '',
    customerQuotedAmount: '',
    advancePayment: '',
    mechanicPayment: '',
    completionMedia: []
  });
  const [uploadingCompletion, setUploadingCompletion] = useState(false);
  const [showEditChooserModal, setShowEditChooserModal] = useState(false);
  const [showFinancialModal, setShowFinancialModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [financialEditData, setFinancialEditData] = useState({
    customerQuotedAmount: '',
    advancePayment: '',
    mechanicPayment: ''
  });
  const [customerEditData, setCustomerEditData] = useState({
    customerName: '',
    contactNumber: '',
    pincode: ''
  });
  const [isEditWindowActive, setIsEditWindowActive] = useState(false);
  const [editRemainingSeconds, setEditRemainingSeconds] = useState(0);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedTransferAdvisorId, setSelectedTransferAdvisorId] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);
  const [advisors, setAdvisors] = useState([]);

  const { data: complaintData, isLoading, refetch } = useQuery({
    queryKey: ['serviceAdvisorComplaintDetail', id],
    queryFn: async () => {
      const result = await ServiceAdvisorService.getComplaintById(id);
      return result.data;
    },
    enabled: !!id,
    refetchInterval: (q) => {
      const c = q.state?.data;
      const s = String(c?.request_status || c?.status || '').toLowerCase();
      if (!c?.assigned_mechanic_id) return false;
      if (s.includes('cancel') || s.includes('complete') || s.includes('closed')) return false;
      return 4000;
    },
  });

  const { data: acceptedPartner } = useQuery({
    queryKey: ['complaintAcceptedPartner', complaintData?.id, complaintData?.assigned_mechanic_id],
    queryFn: async () => {
      const db = dbClient();
      const requestId = complaintData.id;
      const [{ data: offers }, { data: msgs }] = await Promise.all([
        db
          .from('service_request_rate_offers')
          .select('mechanic_id, status, amount, created_at, proposed_by, proposed_by_user_id')
          .eq('service_request_id', requestId)
          .order('created_at', { ascending: false })
          .limit(40),
        db
          .from('rsa_job_messages')
          .select('mechanic_id, sender_user_id, sender_role, created_at')
          .eq('service_request_id', requestId)
          .eq('sender_role', 'partner')
          .order('created_at', { ascending: false })
          .limit(30),
      ]);

      const offerRows = offers || [];
      const accepted = offerRows.find((o) =>
        ['accepted', 'agreed'].includes(String(o.status || '').toLowerCase()),
      );
      const agreedFallback =
        !accepted &&
        ['agreed', 'paid', 'collected_cash'].includes(String(complaintData.rate_status || '').toLowerCase())
          ? offerRows.find((o) => o.mechanic_id && String(o.proposed_by || '') !== 'customer')
          : null;
      const chatMechId = (msgs || []).find((m) => m.mechanic_id)?.mechanic_id;
      const mechanicId =
        complaintData.assigned_mechanic_id ||
        accepted?.mechanic_id ||
        agreedFallback?.mechanic_id ||
        chatMechId;
      if (!mechanicId) return null;

      const userId =
        accepted?.proposed_by_user_id ||
        agreedFallback?.proposed_by_user_id ||
        (msgs || []).find((m) => String(m.mechanic_id) === String(mechanicId))?.sender_user_id;

      const [{ data: mech }, { data: userRow }] = await Promise.all([
        db
          .from('mechanics')
          .select('id, shop_name, mechanic_name, number, alternate_number1, service_center_lat, service_center_lng')
          .eq('id', mechanicId)
          .maybeSingle(),
        userId
          ? db.from('users').select('id, name').eq('id', userId).maybeSingle()
          : Promise.resolve({ data: null }),
      ]);

      const displayName = partnerDisplayName(mech, userRow?.name);
      if (!displayName && !mech) return null;
      return {
        ...(mech || {}),
        id: mechanicId,
        display_name: displayName,
      };
    },
    enabled: !!complaintData?.id,
  });

  const { data: bargainPartners = [] } = useQuery({
    queryKey: ['complaintBargainPartners', complaintData?.id],
    queryFn: async () => {
      const db = dbClient();
      const requestId = complaintData.id;
      const { data: pricingRaw } = await db.rpc('rsa_get_job_pricing', { p_request_id: requestId });
      let pricing = pricingRaw;
      if (typeof pricing === 'string') {
        try {
          pricing = JSON.parse(pricing);
        } catch {
          pricing = null;
        }
      }
      const rpcOffers = Array.isArray(pricing?.offers)
        ? pricing.offers
        : Array.isArray(pricing?.data?.offers)
          ? pricing.data.offers
          : [];
      if (rpcOffers.length) return mapBargainOffers(rpcOffers, db, requestId);

      const { data: offers, error } = await db
        .from('service_request_rate_offers')
        .select('mechanic_id, status, amount, created_at, proposed_by')
        .eq('service_request_id', requestId)
        .order('created_at', { ascending: false })
        .limit(40);
      if (error || !offers?.length) return [];
      return mapBargainOffers(offers, db, requestId);
    },
    enabled: !!complaintData?.id,
    refetchInterval: 15000,
  });

  const { data: assignedMechanicRow } = useQuery({
    queryKey: ['serviceAdvisorAssignedMechanic', complaintData?.assigned_mechanic_id],
    queryFn: async () => {
      const res = await ServiceAdvisorService.getMechanicById(complaintData?.assigned_mechanic_id);
      return res.data;
    },
    enabled: !!complaintData?.assigned_mechanic_id,
  });

  const { data: locationPincode } = useQuery({
    queryKey: [
      'complaintLocationPincode',
      complaintData?.id,
      complaintData?.pickup_latitude,
      complaintData?.pickup_longitude,
      complaintData?.location_link,
    ],
    queryFn: async () => {
      const fromText = pincodeFromComplaintText(complaintData);
      if (fromText) return fromText;
      const pin = customerPinFromComplaint(complaintData);
      if (!pin) return '';
      return (await reverseGeocodePincode(pin.lat, pin.lng)) || '';
    },
    enabled: !!complaintData?.id,
    staleTime: 1000 * 60 * 30,
  });

  const { data: vehicleRow } = useQuery({
    queryKey: [
      'complaintLinkedVehicle',
      complaintData?.id,
      complaintData?.vehicle_id,
      complaintData?.vehicle_number,
      complaintData?.customer_id,
    ],
    queryFn: async () => {
      const db = serviceRoleClient || supabase;
      if (complaintData?.vehicle_id) {
        const { data } = await db
          .from('customer_vehicles')
          .select('brand, model, registration_number, vehicle_type, fuel_type')
          .eq('id', complaintData.vehicle_id)
          .maybeSingle();
        if (data) return data;
      }
      const reg = String(complaintData?.vehicle_number || '').trim();
      const customerId = complaintData?.customer_id;
      if (reg && customerId) {
        const { data } = await db
          .from('customer_vehicles')
          .select('brand, model, registration_number, vehicle_type, fuel_type')
          .eq('customer_id', customerId)
          .ilike('registration_number', reg)
          .limit(1);
        if (data?.[0]) return data[0];
      }
      return null;
    },
    enabled: !!complaintData?.id,
  });

  const { data: timelineData } = useQuery({
    queryKey: ['serviceAdvisorComplaintTimeline', id],
    queryFn: async () => {
      const result = await ServiceAdvisorService.getComplaintTimeline(id);
      return result.data || [];
    },
    enabled: !!id,
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ status, notes }) => ServiceAdvisorService.updateComplaintStatus(id, status, notes, user?.id),
    onSuccess: () => {
      alert('Status updated successfully!');
      setShowStatusModal(false);
      setNewStatus('');
      setStatusNotes('');
      refetch();
      queryClient.invalidateQueries(['serviceAdvisorComplaintDetail', id]);
      queryClient.invalidateQueries(['serviceAdvisorComplaintTimeline', id]);
    },
    onError: (error) => {
      alert('Failed to update status: ' + error.message);
    },
  });

  const updatePincodeMutation = useMutation({
    mutationFn: (pincode) => ServiceAdvisorService.updateComplaintPincode(id, pincode),
    onSuccess: () => {
      alert('Pincode updated successfully!');
      setShowPincodeModal(false);
      setPincode('');
      refetch();
    },
    onError: (error) => {
      alert('Failed to update pincode: ' + error.message);
    },
  });

  const complaint = complaintData || {};
  const timeline = timelineData || [];

  const getStatusColor = (status) => {
    const s = status?.toLowerCase() || '';
    if (s === 'completed') return 'bg-green-100 text-green-700';
    if (s === 'cancelled' || s === 'canceled') return 'bg-red-100 text-red-700';
    if (s === 'in_progress') return 'bg-blue-100 text-blue-700';
    if (s === 'assigned') return 'bg-yellow-100 text-yellow-700';
    if (s === 'registered') return 'bg-purple-100 text-purple-700';
    return 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const handleCall = (phoneNumber) => {
    if (phoneNumber) {
      window.location.href = `tel:${phoneNumber}`;
    } else {
      alert('No phone number available');
    }
  };

  const handleWhatsAppLocation = () => {
    if (!complaint.location_link) {
      alert('Location link not available');
      return;
    }
    const message = `Please share your location: ${complaint.location_link}`;
    const whatsappUrl = `https://wa.me/${complaint.contact_number?.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleTransferComplaint = async () => {
    if (!selectedTransferAdvisorId) {
      alert('Please select an advisor');
      return;
    }

    const selectedAdvisor = advisors.find(a => a.id === selectedTransferAdvisorId);
    if (!selectedAdvisor) {
      alert('Selected advisor not found');
      return;
    }

    setIsTransferring(true);
    try {
      const result = await ServiceAdvisorService.transferComplaintToAdvisor(
        id,
        user?.id,
        selectedTransferAdvisorId,
        selectedAdvisor.name || selectedAdvisor.email
      );

      if (result.success) {
        alert(result.message || 'Complaint transferred successfully');
        setShowTransferModal(false);
        setSelectedTransferAdvisorId('');
        refetch();
        queryClient.invalidateQueries(['serviceAdvisorComplaintDetail', id]);
        queryClient.invalidateQueries(['serviceAdvisorComplaints', user?.id]);
      } else {
        alert('Failed to transfer: ' + (result.error || result.message));
      }
    } catch (error) {
      alert('Error transferring complaint: ' + error.message);
    } finally {
      setIsTransferring(false);
    }
  };

  const handleUnassignAdvisor = async () => {
    if (!confirm('Are you sure you want to unassign yourself from this complaint?')) {
      return;
    }

    try {
      const result = await ServiceAdvisorService.unassignComplaint(id, user?.id);
      if (result.success) {
        alert(result.message || 'Complaint unassigned successfully');
        refetch();
        queryClient.invalidateQueries(['serviceAdvisorComplaintDetail', id]);
        queryClient.invalidateQueries(['serviceAdvisorComplaints', user?.id]);
      } else {
        alert('Failed to unassign: ' + (result.error || result.message));
      }
    } catch (error) {
      alert('Error unassigning complaint: ' + error.message);
    }
  };

  const openImage = (url) => {
    setSelectedImage(url);
    setShowImageModal(true);
  };

  const handleUpdateStatus = () => {
    if (!newStatus) {
      alert('Please select a status');
      return;
    }
    updateStatusMutation.mutate({ status: newStatus, notes: statusNotes });
  };

  const handleUpdatePincode = () => {
    if (!pincode.trim()) {
      alert('Please enter a pincode');
      return;
    }
    updatePincodeMutation.mutate(pincode);
  };

  // Load advisors for transfer modal
  useEffect(() => {
    const loadAdvisors = async () => {
      try {
        const result = await ServiceAdvisorService.getAllAdvisors();
        if (result.success && result.data) {
          setAdvisors(result.data);
        }
      } catch (error) {
        console.error('Error loading advisors:', error);
      }
    };
    loadAdvisors();
  }, []);

  // Check if edit window is active (60 minutes after completion)
  useEffect(() => {
    if (complaint?.request_status === 'completed' && complaint?.completed_at) {
      const completedAt = new Date(complaint.completed_at);
      const now = new Date();
      const diffMs = now - completedAt;
      const diffSeconds = Math.floor(diffMs / 1000);
      const remaining = 3600 - diffSeconds; // 60 minutes = 3600 seconds
      
      if (remaining > 0) {
        setIsEditWindowActive(true);
        setEditRemainingSeconds(remaining);
        const interval = setInterval(() => {
          const newRemaining = 3600 - Math.floor((new Date() - completedAt) / 1000);
          if (newRemaining > 0) {
            setEditRemainingSeconds(newRemaining);
          } else {
            setIsEditWindowActive(false);
            setEditRemainingSeconds(0);
            clearInterval(interval);
          }
        }, 1000);
        return () => clearInterval(interval);
      } else {
        setIsEditWindowActive(false);
        setEditRemainingSeconds(0);
      }
    } else {
      setIsEditWindowActive(false);
      setEditRemainingSeconds(0);
    }
  }, [complaint?.request_status, complaint?.completed_at]);

  const formatMMSS = (totalSeconds) => {
    const m = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
    const s = Math.floor(totalSeconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const getTimelineEvents = () => {
    const timelineEvents = [];
    if (!complaint) return timelineEvents;
    if (complaint.register_datetime || complaint.requested_at || complaint.created_at) {
      timelineEvents.push({
        id: 'complaint-registered',
        status: 'Complaint Registered',
        timestamp: complaint.register_datetime || complaint.requested_at || complaint.created_at,
        actor_name: complaint.registered_by_executive_name || 'System',
        actor_role: 'Executive',
        notes: `Complaint registered for ${complaint.customer_name || 'Customer'}`,
        icon: '📝',
        color: '#3b82f6',
      });
    }
    if (complaint.assigned_to_advisor_at || complaint.assigned_advisor_id) {
      const isSelfAllocated = complaint.assigned_advisor_id === user?.id;
      timelineEvents.push({
        id: 'advisor-allocated',
        status: isSelfAllocated ? 'Self Allocated' : 'Allocated to Advisor',
        timestamp: complaint.assigned_to_advisor_at || complaint.register_datetime,
        actor_name: complaint.assigned_advisor_name || (isSelfAllocated ? user?.name : 'Advisor'),
        actor_role: 'Advisor',
        notes: isSelfAllocated ? 'You claimed this complaint' : `Allocated to ${complaint.assigned_advisor_name || 'Advisor'}`,
        icon: isSelfAllocated ? '👤' : '👥',
        color: isSelfAllocated ? '#10b981' : '#f59e0b',
      });
    }
    if (complaint.mechanic_assigned_datetime || complaint.assigned_mechanic_id) {
      timelineEvents.push({
        id: 'mechanic-assigned',
        status: 'Mechanic Assigned',
        timestamp: complaint.mechanic_assigned_datetime || complaint.assigned_to_advisor_at,
        actor_name: complaint.assigned_mechanic_name || 'Mechanic',
        actor_role: 'Mechanic',
        notes: complaint.assigned_remark || `Mechanic ${complaint.assigned_mechanic_name || ''} assigned`,
        icon: '🔧',
        color: '#8b5cf6',
      });
    }
    if (complaint.mechanic_reached_datetime) {
      timelineEvents.push({
        id: 'mechanic-reached',
        status: 'Mechanic Reached',
        timestamp: complaint.mechanic_reached_datetime,
        actor_name: complaint.assigned_mechanic_name || 'Mechanic',
        actor_role: 'Mechanic',
        notes: complaint.reached_remark || 'Mechanic reached the location',
        icon: '📍',
        color: '#06b6d4',
      });
    }
    if (complaint.mechanic_started_datetime) {
      timelineEvents.push({
        id: 'mechanic-started',
        status: 'Work Started',
        timestamp: complaint.mechanic_started_datetime,
        actor_name: complaint.assigned_mechanic_name || 'Mechanic',
        actor_role: 'Mechanic',
        notes: complaint.mechanic_notes || 'Work started by mechanic',
        icon: '⚡',
        color: '#f59e0b',
      });
    }
    if (complaint.mechanic_completed_datetime) {
      timelineEvents.push({
        id: 'mechanic-completed',
        status: 'Work Completed',
        timestamp: complaint.mechanic_completed_datetime,
        actor_name: complaint.assigned_mechanic_name || 'Mechanic',
        actor_role: 'Mechanic',
        notes: complaint.mechanic_completion_notes || complaint.complete_remark || 'Work completed by mechanic',
        icon: '✅',
        color: '#10b981',
      });
    }
    if (complaint.completed_at || complaint.request_status === 'completed') {
      timelineEvents.push({
        id: 'service-completed',
        status: 'Service Completed',
        timestamp: complaint.completed_at || complaint.mechanic_completed_datetime,
        actor_name: complaint.assigned_advisor_name || user?.name || 'Advisor',
        actor_role: 'Advisor',
        notes: complaint.completion_notes || 'Service marked as completed',
        icon: '🎉',
        color: '#10b981',
      });
    }
    if (complaint.cancelled_at || complaint.request_status === 'cancelled') {
      timelineEvents.push({
        id: 'service-cancelled',
        status: 'Service Cancelled',
        timestamp: complaint.cancelled_at || complaint.updated_at,
        actor_name: complaint.assigned_advisor_name || complaint.cancelled_by || 'System',
        actor_role: 'Advisor',
        notes: complaint.cancelled_remark || complaint.cancellation_reason || 'Service cancelled',
        icon: '❌',
        color: '#ef4444',
      });
    }
    if (Array.isArray(timeline) && timeline.length > 0) {
      timeline.forEach((event, index) => {
        const exists = timelineEvents.some(
          (e) => e.timestamp === (event.occurred_at || event.timestamp) && e.status === (event.status || event.event),
        );
        if (!exists) {
          timelineEvents.push({
            id: `timeline-${index}`,
            status: event.status || event.event || 'Status Update',
            timestamp: event.occurred_at || event.created_at || event.timestamp,
            actor_name: event.actor_name || 'System',
            actor_role: event.actor_role || 'System',
            notes: event.notes || event.remark || event.description || '',
            icon: '📋',
            color: '#6b7280',
          });
        }
      });
    }
    return timelineEvents.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  };

  const handleCompleteComplaint = async () => {
    if (!completionData.completionNotes.trim()) {
      alert('Please enter completion notes');
      return;
    }

    setUploadingCompletion(true);
    try {
      const result = await ServiceAdvisorService.updateComplaintCompletion(
        id,
        {
          completionNotes: completionData.completionNotes,
          customerQuotedAmount: completionData.customerQuotedAmount ? parseFloat(completionData.customerQuotedAmount) : undefined,
          advancePayment: completionData.advancePayment || undefined,
          mechanicPayment: completionData.mechanicPayment ? parseFloat(completionData.mechanicPayment) : undefined,
          completionMedia: completionData.completionMedia || undefined
        },
        user?.id,
        user?.name || user?.email
      );

      if (result.success) {
        alert(result.message || 'Service completed successfully!');
        setShowCompletionModal(false);
        setCompletionData({
          completionNotes: '',
          customerQuotedAmount: '',
          advancePayment: '',
          mechanicPayment: '',
          completionMedia: []
        });
        refetch();
        queryClient.invalidateQueries(['serviceAdvisorComplaintDetail', id]);
      } else {
        alert('Failed to complete service: ' + result.message);
      }
    } catch (error) {
      alert('Error completing service: ' + error.message);
    } finally {
      setUploadingCompletion(false);
    }
  };

  const handleAddCompletionMedia = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      const fileUrls = files.map(file => URL.createObjectURL(file));
      setCompletionData(prev => ({
        ...prev,
        completionMedia: [...prev.completionMedia, ...fileUrls]
      }));
    };
    input.click();
  };

  const handleRemoveCompletionMedia = (index) => {
    setCompletionData(prev => ({
      ...prev,
      completionMedia: prev.completionMedia.filter((_, i) => i !== index)
    }));
  };

  const handleSaveFinancialEdit = async () => {
    try {
      const result = await ServiceAdvisorService.editCompletedComplaintWithinWindow(
        id,
        {
          customerQuotedAmount: financialEditData.customerQuotedAmount ? parseFloat(financialEditData.customerQuotedAmount) : undefined,
          advancePayment: financialEditData.advancePayment || undefined,
          mechanicPayment: financialEditData.mechanicPayment ? parseFloat(financialEditData.mechanicPayment) : undefined
        },
        user?.id,
        user?.name || user?.email
      );

      if (result.success) {
        alert('Financial details updated successfully!');
        setShowFinancialModal(false);
        refetch();
      } else {
        alert('Failed to update: ' + result.message);
      }
    } catch (error) {
      alert('Error updating financial details: ' + error.message);
    }
  };

  const handleSaveCustomerEdit = async () => {
    try {
      const result = await ServiceAdvisorService.editCompletedComplaintWithinWindow(
        id,
        {
          customerName: customerEditData.customerName || undefined,
          contactNumber: customerEditData.contactNumber || undefined,
          pincode: customerEditData.pincode || undefined
        },
        user?.id,
        user?.name || user?.email
      );

      if (result.success) {
        alert('Customer details updated successfully!');
        setShowCustomerModal(false);
        refetch();
      } else {
        alert('Failed to update: ' + result.message);
      }
    } catch (error) {
      alert('Error updating customer details: ' + error.message);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full spinner"></div>
      </div>
    );
  }

  if (!complaint || !complaint.id) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center p-6 bg-white rounded-lg shadow-md">
          <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Complaint Not Found</h2>
          <p className="text-gray-600">The complaint with ID "{id}" could not be found.</p>
          <button onClick={() => navigate(-1)} className="mt-4 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const statusRaw = complaint.request_status || complaint.status || 'Unknown';
  const isCancelled = String(statusRaw).toLowerCase().includes('cancel') || !!complaint.cancelled_at;
  const problem = complaint.problem || complaint.service_description || complaint.issue_description || 'Not specified';
  const registeredAt = complaint.complaint_registered_at || complaint.register_datetime || complaint.requested_at || complaint.created_at;
  const towingJob = isTowingComplaint(complaint);
  const pickupText = pickupAddressText(complaint);
  const dropText = dropAddressText(complaint);
  const pickupMapHref =
    mapsSearchHref(pickupText, complaint.pickup_latitude, complaint.pickup_longitude) ||
    (complaint.location_link
      ? complaint.location_link.startsWith('http')
        ? complaint.location_link
        : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(complaint.location_link)}`
      : '');
  const dropMapHref = mapsSearchHref(dropText, complaint.drop_latitude, complaint.drop_longitude);
  const pickupPin = pinFromPickup(complaint);
  const dropPin = pinFromDrop(complaint);
  const towDistanceKm = haversineKm(pickupPin, dropPin);
  const towDistanceLabel = formatKm(towDistanceKm);
  const towRouteHref = mapsDirHref(pickupPin, pickupText, dropPin, dropText);
  const addressText =
    pickupText ||
    (complaint.location_link ? 'Location link available' : '') ||
    dropText ||
    'N/A';
  const timelineEvents = getTimelineEvents();
  const vehicleBrand = String(complaint.vehicle_brand || vehicleRow?.brand || '').trim();
  const vehicleModel = String(
    complaint.vehicle_model || complaint.vehicle_details || vehicleRow?.model || '',
  ).trim();
  const vehicleNumber = String(
    complaint.vehicle_number || vehicleRow?.registration_number || '',
  ).trim();
  const vehicleType = String(complaint.vehicle_type || vehicleRow?.vehicle_type || '').trim();
  const vehicleFuel = String(complaint.fuel_type || vehicleRow?.fuel_type || '').trim();
  const vehicleLabel = [vehicleBrand, vehicleModel].filter(Boolean).join(' ') || vehicleModel || 'Not specified';
  const mechanicRow = assignedMechanicRow || acceptedPartner || null;
  const mechanicName =
    String(complaint.assigned_mechanic_name || complaint.mechanic_name || '').trim() ||
    acceptedPartner?.display_name ||
    partnerDisplayName(mechanicRow) ||
    'Not assigned';
  const displayPincode = String(complaint.pincode || '').trim() || locationPincode || '';
  const mechanicPhone = complaint.assigned_mechanic_contact || mechanicRow?.number || '';
  const actionBtn = (cls) =>
    `inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-white ${cls}`;
  const Info = ({ label, children }) => (
    <div className="min-w-0">
      <p className="text-[11px] leading-tight text-slate-400">{label}</p>
      <div className="mt-0.5 text-sm font-semibold text-slate-900 break-words">{children}</div>
    </div>
  );

  return (
    <div className="min-h-full bg-slate-100/80 -mx-4 sm:-mx-5 lg:-mx-6 -mt-4 sm:-mt-5 lg:-mt-6 -mb-8 px-4 sm:px-5 lg:px-6 pt-4 sm:pt-5 lg:pt-6 pb-8">
      <div className={`border-b ${isCancelled ? 'border-red-200 bg-gradient-to-r from-red-50 to-white' : 'border-slate-200 bg-white'}`}>
        <div className="px-4 py-3 lg:px-5">
          <div className="flex flex-wrap items-start gap-3">
            <button onClick={() => navigate(-1)} className="mt-0.5 rounded-lg p-2 hover:bg-slate-100" title="Back">
              <ArrowLeft size={20} className="text-slate-700" />
            </button>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-bold text-slate-900 sm:text-2xl">
                  {complaint.customer_name || 'Complaint'}
                </h1>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide ${getStatusColor(statusRaw)}`}>
                  {statusRaw}
                </span>
                <span className="font-mono text-xs text-slate-400">#{complaint.id?.slice(0, 8)}</span>
              </div>
              <p className="mt-1 text-sm font-medium text-slate-800">{problem}</p>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1">
                  <Clock size={12} /> Registered {formatDate(registeredAt)}
                </span>
                {isCancelled ? (
                  <span className="inline-flex items-center gap-1 font-semibold text-red-600">
                    <X size={12} /> Cancelled {formatDate(complaint.cancelled_at || complaint.updated_at)}
                  </span>
                ) : null}
                {complaint.source ? (
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold uppercase tracking-wide text-slate-600">
                    {String(complaint.source).replace(/_/g, ' ')}
                  </span>
                ) : null}
                {complaint.contact_number ? (
                  <span className="inline-flex items-center gap-1 font-medium text-slate-700">
                    <Phone size={12} /> {complaint.contact_number}
                  </span>
                ) : null}
                {vehicleLabel !== 'Not specified' || vehicleNumber ? (
                  <span className="inline-flex items-center gap-1 font-medium text-slate-700">
                    <Car size={12} /> {[vehicleLabel !== 'Not specified' ? vehicleLabel : null, vehicleNumber].filter(Boolean).join(' · ')}
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={() => {
                setNewStatus(complaint.request_status || 'registered');
                setShowStatusModal(true);
              }}
              className={actionBtn('bg-teal-600 hover:bg-teal-700')}
            >
              <Edit size={15} /> Status
            </button>
            {!complaint.assigned_mechanic_id ? (
              <button
                onClick={() => navigate(`/service_advisor/assign-mechanic/${id}`, { state: { complaint } })}
                className={actionBtn('bg-blue-600 hover:bg-blue-700')}
              >
                <Wrench size={15} /> Assign mechanic
              </button>
            ) : (
              <>
                <button onClick={() => navigate(`/service_advisor/assignment-details/${id}`)} className={actionBtn('bg-blue-600 hover:bg-blue-700')}>
                  <User size={15} /> Assignment
                </button>
                <button
                  onClick={async () => {
                    if (!confirm('Unassign the current mechanic?')) return;
                    try {
                      const result = await ServiceAdvisorService.unassignMechanicFromRequest(id, user?.id, user?.name || user?.email);
                      if (result.success) {
                        alert(result.message || 'Mechanic unassigned successfully');
                        refetch();
                        queryClient.invalidateQueries(['serviceAdvisorComplaintDetail', id]);
                      } else {
                        alert('Failed to unassign mechanic: ' + (result.error || result.message));
                      }
                    } catch (error) {
                      alert('Error unassigning mechanic: ' + error.message);
                    }
                  }}
                  className={actionBtn('bg-red-500 hover:bg-red-600')}
                >
                  <X size={15} /> Unassign mechanic
                </button>
              </>
            )}
            {complaint.request_status !== 'completed' && complaint.request_status !== 'cancelled' ? (
              <button
                onClick={() => {
                  setCompletionData({
                    completionNotes: '',
                    customerQuotedAmount: complaint.customer_quoted_amount?.toString() || '',
                    advancePayment: complaint.advance_payment || '',
                    mechanicPayment: complaint.payment_mechanic?.toString() || '',
                    completionMedia: [],
                  });
                  setShowCompletionModal(true);
                }}
                className={actionBtn('bg-emerald-600 hover:bg-emerald-700')}
              >
                <CheckCircle size={15} /> Complete
              </button>
            ) : null}
            {isEditWindowActive ? (
              <button onClick={() => setShowEditChooserModal(true)} className={actionBtn('bg-purple-600 hover:bg-purple-700')}>
                <Edit size={15} /> Edit {formatMMSS(editRemainingSeconds)}
              </button>
            ) : null}
            {complaint.assigned_advisor_id === user?.id ? (
              <>
                <button onClick={() => setShowTransferModal(true)} className={actionBtn('bg-indigo-600 hover:bg-indigo-700')}>
                  <User size={15} /> Transfer
                </button>
                <button onClick={handleUnassignAdvisor} className={actionBtn('bg-orange-500 hover:bg-orange-600')}>
                  <X size={15} /> Unassign advisor
                </button>
              </>
            ) : null}
            {complaint.location_link && complaint.contact_number ? (
              <button onClick={handleWhatsAppLocation} className={actionBtn('bg-green-600 hover:bg-green-700')}>
                <MessageCircle size={15} /> WhatsApp location
              </button>
            ) : null}
            {complaint.contact_number ? (
              <button onClick={() => handleCall(complaint.contact_number)} className={actionBtn('bg-slate-800 hover:bg-slate-900')}>
                <Phone size={15} /> Customer
              </button>
            ) : null}
            {complaint.assigned_mechanic_contact ? (
              <button onClick={() => handleCall(complaint.assigned_mechanic_contact)} className={actionBtn('bg-slate-700 hover:bg-slate-800')}>
                <Phone size={15} /> Mechanic
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="space-y-3 p-3 lg:p-4">
        <div className="grid gap-3 lg:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
              <Info label="Name">{complaint.customer_name || 'N/A'}</Info>
              <Info label="Contact">
                <span className="inline-flex items-center gap-1.5">
                  {complaint.contact_number || 'N/A'}
                  {complaint.contact_number ? (
                    <>
                      <button onClick={() => copyToClipboard(complaint.contact_number)} className="rounded p-0.5 hover:bg-slate-100" title="Copy">
                        <Copy size={12} className="text-slate-400" />
                      </button>
                      <button onClick={() => handleCall(complaint.contact_number)} className="rounded p-0.5 hover:bg-slate-100" title="Call">
                        <Phone size={12} className="text-blue-500" />
                      </button>
                    </>
                  ) : null}
                </span>
              </Info>
              {complaint.alternate_number ? (
                <Info label="Alternate">
                  <span className="inline-flex items-center gap-1.5">
                    {complaint.alternate_number}
                    <button onClick={() => copyToClipboard(complaint.alternate_number)} className="rounded p-0.5 hover:bg-slate-100">
                      <Copy size={12} className="text-slate-400" />
                    </button>
                    <button onClick={() => handleCall(complaint.alternate_number)} className="rounded p-0.5 hover:bg-slate-100">
                      <Phone size={12} className="text-blue-500" />
                    </button>
                  </span>
                </Info>
              ) : null}
              <Info label="Vehicle model">
                {vehicleLabel}
                {vehicleType || vehicleFuel ? (
                  <span className="block text-xs font-normal text-slate-500">{[vehicleType, vehicleFuel].filter(Boolean).join(' · ')}</span>
                ) : null}
              </Info>
              <Info label="Vehicle number">{vehicleNumber || 'Not set'}</Info>
              <Info label="Pincode">
                <span className="inline-flex items-center gap-1.5">
                  {displayPincode || 'Not set'}
                  <button
                    onClick={() => {
                      setPincode(displayPincode || '');
                      setShowPincodeModal(true);
                    }}
                    className="rounded p-0.5 hover:bg-slate-100"
                  >
                    <Edit size={12} className="text-slate-400" />
                  </button>
                </span>
              </Info>
              <Info label="Mechanic">{mechanicName}</Info>
              {mechanicPhone ? (
                <Info label="Mechanic phone">
                  <span className="inline-flex items-center gap-1.5">
                    {mechanicPhone}
                    <button onClick={() => handleCall(mechanicPhone)} className="rounded p-0.5 hover:bg-slate-100">
                      <Phone size={12} className="text-blue-500" />
                    </button>
                  </span>
                </Info>
              ) : null}
              {complaint.customer_quoted_amount ? (
                <Info label="Quoted">₹{complaint.customer_quoted_amount}</Info>
              ) : null}
              {complaint.advance_payment ? <Info label="Advance">₹{complaint.advance_payment}</Info> : null}
              {(complaint.payment_mechanic !== null && complaint.payment_mechanic !== undefined) ? (
                <Info label="Mechanic pay">₹{complaint.payment_mechanic || '0'}</Info>
              ) : null}
              <div className="col-span-2 sm:col-span-3">
                {towingJob ? (
                  <>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Info label="Pickup">
                      {pickupText || 'Not set'}
                      {pickupMapHref ? (
                        <a
                          href={pickupMapHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-teal-600 hover:underline"
                        >
                          Map <ExternalLink size={12} />
                        </a>
                      ) : null}
                    </Info>
                    <Info label="Drop">
                      {dropText || 'Not set'}
                      {dropMapHref ? (
                        <a
                          href={dropMapHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-teal-600 hover:underline"
                        >
                          Map <ExternalLink size={12} />
                        </a>
                      ) : null}
                    </Info>
                  </div>
                  {towRouteHref || towDistanceLabel ? (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {towDistanceLabel ? (
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                          Distance {towDistanceLabel}
                        </span>
                      ) : null}
                      {towRouteHref ? (
                        <a
                          href={towRouteHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-lg bg-[#1B4F9C] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#153E7A]"
                        >
                          <Navigation size={13} />
                          Open route
                        </a>
                      ) : null}
                    </div>
                  ) : null}
                  </>
                ) : (
                  <Info label="Address">
                    {addressText}
                    {pickupMapHref ? (
                      <a
                        href={pickupMapHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-teal-600 hover:underline"
                      >
                        Map <ExternalLink size={12} />
                      </a>
                    ) : null}
                  </Info>
                )}
              </div>
              {complaint.remark ? (
                <div className="col-span-2 sm:col-span-3">
                  <Info label="Notes">{complaint.remark}</Info>
                </div>
              ) : null}
              {complaint.assigned_remark ? (
                <div className="col-span-2 sm:col-span-3">
                  <Info label="Assignment remark">{complaint.assigned_remark}</Info>
                </div>
              ) : null}
            </div>
            {isCancelled ? (
              <div className="mt-3 rounded-md border border-red-100 bg-red-50 px-3 py-2 text-sm">
                <span className="font-semibold text-red-700">Cancelled {formatDate(complaint.cancelled_at || complaint.updated_at)}</span>
                {(complaint.cancelled_remark || complaint.cancellation_reason) ? (
                  <span className="text-red-800"> — {complaint.cancelled_remark || complaint.cancellation_reason}</span>
                ) : null}
              </div>
            ) : null}
            {complaint.media_upload && complaint.media_upload.length > 0 ? (
              <div className="mt-3 grid grid-cols-5 gap-2">
                {complaint.media_upload.map((url, index) => (
                  <button
                    key={index}
                    onClick={() => openImage(url)}
                    className="relative aspect-square overflow-hidden rounded-md border border-slate-200 hover:border-teal-500"
                  >
                    <img src={url} alt={`Media ${index + 1}`} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">
              Mechanic map
            </p>
            <ComplaintMechanicMap
              complaint={complaint}
              mechanicRow={mechanicRow}
              bargainPartners={bargainPartners}
              embedded
              compact
            />
          </div>
        </div>

        <div className="grid gap-3 lg:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">Customer chat</p>
            <ComplaintJobChat
              requestId={complaint.id}
              phone={complaint.contact_number}
              customerName={complaint.customer_name}
              assignedMechanicId={complaint.assigned_mechanic_id}
              embedded
            />
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">Call recordings</p>
            <ComplaintCallRecordings
              requestId={complaint.id}
              customerPhone={complaint.contact_number || complaint.customer_phone || complaint.phone}
              alternatePhone={complaint.alternate_number}
              mechanicPhone={mechanicPhone}
              mechanicAltPhone={mechanicRow?.alternate_number1 || mechanicRow?.alternate_number}
              registeredAt={registeredAt}
              cancelledAt={complaint.cancelled_at}
              completedAt={complaint.completed_at || complaint.mechanic_completed_datetime}
              embedded
            />
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="mb-3 text-[11px] font-bold uppercase tracking-wider text-slate-400">Status timeline</p>
          {timelineEvents.length === 0 ? (
            <p className="text-sm text-slate-500">No timeline events yet.</p>
          ) : (
            <ol className="relative ml-2 border-l border-slate-200">
              {timelineEvents.map((event, index) => (
                <li key={event.id || index} className="mb-3 ml-5 last:mb-0">
                  <span
                    className="absolute -left-2.5 flex h-5 w-5 items-center justify-center rounded-full ring-4 ring-white"
                    style={{ backgroundColor: `${event.color}20`, color: event.color }}
                  >
                    <span className="text-[10px]">{event.icon || '📋'}</span>
                  </span>
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <h3 className="text-sm font-semibold text-slate-900">{event.status}</h3>
                    <time className="text-xs text-slate-400">{formatDate(event.timestamp)}</time>
                  </div>
                  {event.actor_name ? (
                    <p className="text-xs text-slate-500">
                      {event.actor_role}: {event.actor_name}
                    </p>
                  ) : null}
                  {event.notes && event.notes.trim() !== '' ? (
                    <p className="mt-0.5 text-xs text-slate-600">{event.notes}</p>
                  ) : null}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      {/* Update Status Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Update Status</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Select status...</option>
                  <option value="registered">Registered</option>
                  <option value="assigned">Assigned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Add notes (optional)..."
                />
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowStatusModal(false);
                    setNewStatus('');
                    setStatusNotes('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateStatus}
                  disabled={!newStatus || updateStatusMutation.isLoading}
                  className="flex-1 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {updateStatusMutation.isLoading ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                  <span>Save</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Update Pincode Modal */}
      {showPincodeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Update Pincode</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
                <input
                  type="text"
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Enter pincode..."
                />
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowPincodeModal(false);
                    setPincode('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdatePincode}
                  disabled={!pincode.trim() || updatePincodeMutation.isLoading}
                  className="flex-1 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {updatePincodeMutation.isLoading ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                  <span>Save</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4" onClick={() => setShowImageModal(false)}>
          <div className="relative max-w-4xl max-h-[90vh]">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-300"
            >
              <X size={24} />
            </button>
            <img src={selectedImage} alt="Media" className="max-w-full max-h-[90vh] rounded-lg" />
          </div>
        </div>
      )}

      {/* Complete Service Modal */}
      {showCompletionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Complete Service</h2>
              <button onClick={() => setShowCompletionModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Completion Notes *</label>
                <textarea
                  value={completionData.completionNotes}
                  onChange={(e) => setCompletionData(prev => ({ ...prev, completionNotes: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Describe the work completed..."
                />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Financial Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Customer Quoted (₹)</label>
                    <input
                      type="number"
                      value={completionData.customerQuotedAmount}
                      onChange={(e) => setCompletionData(prev => ({ ...prev, customerQuotedAmount: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Advance Payment (₹)</label>
                    <input
                      type="text"
                      value={completionData.advancePayment}
                      onChange={(e) => setCompletionData(prev => ({ ...prev, advancePayment: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mechanic Payment (₹)</label>
                    <input
                      type="number"
                      value={completionData.mechanicPayment}
                      onChange={(e) => setCompletionData(prev => ({ ...prev, mechanicPayment: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Completion Media</h3>
                <button
                  onClick={handleAddCompletionMedia}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center space-x-2"
                >
                  <Upload size={18} />
                  <span>Add Photos</span>
                </button>
                {completionData.completionMedia.length > 0 && (
                  <div className="mt-4 grid grid-cols-4 gap-2">
                    {completionData.completionMedia.map((url, index) => (
                      <div key={index} className="relative">
                        <img src={url} alt={`Media ${index + 1}`} className="w-full h-24 object-cover rounded-lg" />
                        <button
                          onClick={() => handleRemoveCompletionMedia(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowCompletionModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCompleteComplaint}
                  disabled={!completionData.completionNotes.trim() || uploadingCompletion}
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {uploadingCompletion ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />}
                  <span>Complete Service</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Chooser Modal */}
      {showEditChooserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Edit Complaint</h2>
              <button onClick={() => setShowEditChooserModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-3">
              <button
                onClick={() => {
                  setCustomerEditData({
                    customerName: complaint.customer_name || '',
                    contactNumber: complaint.contact_number || '',
                    pincode: complaint.pincode || ''
                  });
                  setShowEditChooserModal(false);
                  setShowCustomerModal(true);
                }}
                className="w-full px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-left"
              >
                <div className="font-semibold">Customer Details</div>
                <div className="text-sm opacity-90">Edit name, phone, pincode</div>
              </button>
              <button
                onClick={() => {
                  setFinancialEditData({
                    customerQuotedAmount: complaint.customer_quoted_amount?.toString() || '',
                    advancePayment: complaint.advance_payment || '',
                    mechanicPayment: complaint.payment_mechanic?.toString() || ''
                  });
                  setShowEditChooserModal(false);
                  setShowFinancialModal(true);
                }}
                className="w-full px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 text-left"
              >
                <div className="font-semibold">Financial Details</div>
                <div className="text-sm opacity-90">Edit quoted amount, payments</div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Financial Edit Modal */}
      {showFinancialModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Edit Financial Details</h2>
              <button onClick={() => setShowFinancialModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Customer Quoted (₹)</label>
                <input
                  type="number"
                  value={financialEditData.customerQuotedAmount}
                  onChange={(e) => setFinancialEditData(prev => ({ ...prev, customerQuotedAmount: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Advance Payment (₹)</label>
                <input
                  type="text"
                  value={financialEditData.advancePayment}
                  onChange={(e) => setFinancialEditData(prev => ({ ...prev, advancePayment: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mechanic Payment (₹)</label>
                <input
                  type="number"
                  value={financialEditData.mechanicPayment}
                  onChange={(e) => setFinancialEditData(prev => ({ ...prev, mechanicPayment: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowFinancialModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveFinancialEdit}
                  className="flex-1 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 flex items-center justify-center space-x-2"
                >
                  <Save size={18} />
                  <span>Save</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Edit Modal */}
      {showCustomerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Edit Customer Details</h2>
              <button onClick={() => setShowCustomerModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Customer Name</label>
                <input
                  type="text"
                  value={customerEditData.customerName}
                  onChange={(e) => setCustomerEditData(prev => ({ ...prev, customerName: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number</label>
                <input
                  type="text"
                  value={customerEditData.contactNumber}
                  onChange={(e) => setCustomerEditData(prev => ({ ...prev, contactNumber: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
                <input
                  type="text"
                  value={customerEditData.pincode}
                  onChange={(e) => setCustomerEditData(prev => ({ ...prev, pincode: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowCustomerModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCustomerEdit}
                  className="flex-1 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 flex items-center justify-center space-x-2"
                >
                  <Save size={18} />
                  <span>Save</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Complaint Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Transfer Complaint</h2>
              <button
                onClick={() => {
                  setShowTransferModal(false);
                  setSelectedTransferAdvisorId('');
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={24} className="text-gray-500" />
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Advisor to Transfer To
              </label>
              <select
                value={selectedTransferAdvisorId}
                onChange={(e) => setSelectedTransferAdvisorId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Select an advisor...</option>
                {advisors
                  .filter(advisor => advisor.id !== user?.id)
                  .map((advisor) => (
                    <option key={advisor.id} value={advisor.id}>
                      {advisor.name || advisor.email} {advisor.id === complaint.assigned_advisor_id ? '(Currently Assigned)' : ''}
                    </option>
                  ))}
              </select>
            </div>
            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => {
                  setShowTransferModal(false);
                  setSelectedTransferAdvisorId('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                disabled={isTransferring}
              >
                Cancel
              </button>
              <button
                onClick={handleTransferComplaint}
                disabled={!selectedTransferAdvisorId || isTransferring}
                className="flex-1 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isTransferring ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>Transferring...</span>
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    <span>Transfer</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplaintDetail;

